import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { createReactRenderer } from 'root/apps/helpers/react-renderer';

import { ConfirmationModal } from './ConfirmationModal';

const WorkOrderCompleteAllModal: React.FC<{
  transactionID: number;
  customerID: number;
}> = ({ transactionID, customerID }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [currentWorkorder, setCurrentWorkorder] = React.useState(0);

  const accountID = String(window.merchantos.session.getSystemCustomerID() ?? '') || undefined;

  const { data: workorderStatuses = [] } = useQuery({
    queryKey: ['workorder-statuses', accountID],
    queryFn: async () => {
      const { data } = await axios.get(`/API/V3/Account/${accountID}/WorkorderStatus.json`);
      return data?.WorkorderStatus ?? [];
    },
    enabled: !!accountID,
  });

  const fetchAllWorkorders = async (url: string, allWorkorders: any[] = []): Promise<any[]> => {
    const { data } = await axios.get(url);

    let newWorkorders: any[] = [];

    if (Array.isArray(data?.Workorder)) {
      newWorkorders = data.Workorder;
    } else if (data?.Workorder) {
      newWorkorders = [data.Workorder];
    }

    const nextUrl = data?.['@attributes']?.next;

    const combined = [...allWorkorders, ...newWorkorders];

    if (nextUrl) {
      return fetchAllWorkorders(nextUrl, combined);
    }

    return combined;
  };

  const { data: workorders = [] } = useQuery({
    queryKey: ['workorders', customerID],
    queryFn: () => {
      const initialUrl = `/API/V3/Account/${accountID}/Workorder.json?customerID=${customerID}&saleID!%3D=${transactionID}`;
      return fetchAllWorkorders(initialUrl);
    },
    enabled: !!customerID && !!accountID,
  });

  const excludedStatusIDs = React.useMemo(() => {
    return workorderStatuses
      .filter((status: { systemValue: string }) =>
        ['estimate', 'paid'].includes(status.systemValue)
      )
      .map((status: { workorderStatusID: string }) => status.workorderStatusID);
  }, [workorderStatuses]);

  const filteredWorkorders = React.useMemo(() => {
    return workorders.filter(
      (wo: { workorderStatusID: string; saleID: string }) =>
        !excludedStatusIDs.includes(wo.workorderStatusID) && wo.saleID === '0'
    );
  }, [workorders, excludedStatusIDs]);

  const checkoutMutation = useMutation({
    mutationFn: async (workorderID: number) => {
      await axios.post(`/API/Account/${accountID}/Workorder/${workorderID}/Checkout.json`, {
        saleID: transactionID,
      });
    },
  });

  const completeWorkOrdersInParallel = async (ids: number[]) => {
    const successIds: number[] = [];
    const failedIds: number[] = [];
    const total = ids.length;

    let processed = 0;

    const results = await Promise.allSettled(
      ids.map((id) =>
        checkoutMutation
          .mutateAsync(id)
          .then((res) => {
            successIds.push(id);
            return res;
          })
          .catch((err) => {
            failedIds.push(id);
            return err;
          })
          .finally(() => {
            processed += 1;
            setCurrentWorkorder(processed);
            setProgress(Math.round((processed / total) * 100));
          })
      )
    );

    return {
      successIds,
      failedIds,
      results,
    };
  };

  const handleDismiss = () => {
    setIsOpen(false);
    setProgress(0);
    setCurrentWorkorder(0);
  };

  const handleCompleteAllWorkOrder = async () => {
    const workorderIDsToComplete = filteredWorkorders.map((wo) => Number(wo.workorderID));

    if (workorderIDsToComplete.length === 0) {
      setIsOpen(false);
      window.alertUser('No eligible workorders to complete.', 100);
      return;
    }

    const { successIds, failedIds } = await completeWorkOrdersInParallel(workorderIDsToComplete);

    if (failedIds.length > 0) {
      window.alertUser(`Some workorders were not able to be completed`, 100);
    }

    if (successIds.length > 0 && window?.merchantos?.register?.changeView) {
      window.merchantos.register.changeView('sale');
    } else {
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        className="gui-def-button"
        style={{ fontWeight: 500 }}
        onClick={() => setIsOpen(true)}
        data-test="work-order-complete-all-btn"
      >
        Complete All
      </Button>
      <ConfirmationModal
        isOpen={isOpen}
        progress={progress}
        currentWorkorder={currentWorkorder}
        totalWorkorders={filteredWorkorders.length}
        handleDismiss={handleDismiss}
        handleCompleteAllWorkOrder={handleCompleteAllWorkOrder}
      />
    </>
  );
};

export default WorkOrderCompleteAllModal;

const RenderedComponent = createReactRenderer({
  component: WorkOrderCompleteAllModal,
  selector: '[data-react-root="WorkOrderCompleteAllModal"]',
});

history.onChangeState(
  [{ pathname: '/register.php', view: 'workorder' }],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
